import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Archive, ArchiveRestore, Trash2, Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { FINAL_STATUSES } from "@/lib/statuses";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/tracking/StatusBadge";
import { Timeline } from "@/components/tracking/Timeline";
import { ShipmentForm } from "@/components/admin/ShipmentForm";
import { EventForm } from "@/components/admin/EventForm";
import { SharePanel } from "@/components/admin/SharePanel";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-slate-500">{label}</span><span className="font-medium text-right break-words min-w-0">{value || "—"}</span>
  </div>
);

const NotifLog = ({ code }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/notifications-log", { params: { code } }).then((r) => setItems(r.data));
  }, [code]);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="notif-log-empty">Nessuna notifica per questa spedizione.</p>;
  return (
    <ul className="divide-y divide-slate-100 text-sm" data-testid="notif-log-list">
      {items.map((n) => (
        <li key={n.id} className="py-2.5 flex flex-wrap gap-x-4 gap-y-1">
          <span className="font-mono text-xs text-slate-500">{fmtDateTime(n.created_at)}</span>
          <span className="uppercase text-xs font-bold">{n.channel}</span>
          <span className={`text-xs font-semibold ${n.status === "inviata" ? "text-emerald-600" : n.status === "errore" ? "text-red-600" : "text-slate-500"}`}>{n.status}</span>
          <span className="text-slate-600 w-full sm:w-auto">{n.detail}</span>
        </li>
      ))}
    </ul>
  );
};

export default function ShipmentDetail() {
  const { code } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const load = useCallback(() => {
    api.get(`/admin/shipments/${code}`).then((r) => setS(r.data)).catch((e) => setError(formatApiError(e)));
  }, [code]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="p-10 text-center" data-testid="shipment-detail-error"><p className="text-slate-600">{error}</p><Link to="/admin/spedizioni" className="text-[#E50914] font-semibold">Torna alle spedizioni</Link></div>;
  if (!s) return <div className="py-32 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#E50914]" /></div>;

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/shipments/${s.code}`, payload);
      setS(data);
      setFormKey((k) => k + 1);
      toast.success("Modifiche salvate");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const archive = async (archived) => {
    try {
      await api.post(`/admin/shipments/${s.code}/archive`, { archived });
      toast.success(archived ? "Spedizione archiviata" : "Spedizione ripristinata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/shipments/${s.code}`);
      toast.success("Spedizione eliminata");
      navigate("/admin/spedizioni");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const pdf = () => downloadFile(`/admin/shipments/${s.code}/pdf`, {}, `scheda-${s.code}.pdf`).catch(() => toast.error("Generazione PDF non riuscita"));
  const isFinal = FINAL_STATUSES.includes(s.status);

  return (
    <div data-testid="admin-shipment-detail">
      <Link to="/admin/spedizioni" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900" data-testid="detail-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Spedizioni</Link>
      {sp.get("nuova") && (
        <div className="mt-4 flex items-start gap-3 bg-emerald-50 border border-emerald-200 p-4" data-testid="new-shipment-banner">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-emerald-800"><strong>Spedizione creata.</strong> Codice tracking generato: <span className="font-mono font-bold">{s.code}</span>. Copialo o invialo al cliente qui sotto.</div>
          <button onClick={() => setSp({})} className="text-emerald-700 text-xs underline">Chiudi</button>
        </div>
      )}
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-2xl sm:text-3xl font-bold" data-testid="detail-code">{s.code}</h1>
          <StatusBadge status={s.status} size="lg" testId="detail-status-badge" />
          {s.archived && <span className="text-xs font-bold uppercase tracking-wider bg-slate-200 px-2 py-1" data-testid="detail-archived-badge">Archiviata</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-none" onClick={() => window.open(`/traccia/${s.code}`, "_blank")} data-testid="detail-view-public-btn"><ExternalLink className="h-4 w-4 mr-1" /> Pagina pubblica</Button>
          <Button variant="outline" className="rounded-none" onClick={pdf} data-testid="detail-pdf-btn"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
          {s.archived ? (
            <Button variant="outline" className="rounded-none" onClick={() => archive(false)} data-testid="detail-unarchive-btn"><ArchiveRestore className="h-4 w-4 mr-1" /> Ripristina</Button>
          ) : (
            <Button variant="outline" className="rounded-none" disabled={!isFinal} title={isFinal ? "" : "Disponibile solo per spedizioni concluse"} onClick={() => archive(true)} data-testid="detail-archive-btn"><Archive className="h-4 w-4 mr-1" /> Archivia</Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="rounded-none text-red-600 hover:text-red-700" data-testid="detail-delete-btn"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Eliminare la spedizione {s.code}?</AlertDialogTitle><AlertDialogDescription>L'operazione è definitiva: la spedizione e la sua cronologia non saranno più consultabili. Per le spedizioni concluse è preferibile archiviare.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel data-testid="delete-cancel-btn">Annulla</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">Elimina</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 order-2 xl:order-1">
          <Tabs defaultValue="events">
            <TabsList className="rounded-none bg-slate-200/70 w-full sm:w-auto">
              <TabsTrigger value="events" className="rounded-none" data-testid="tab-events">Stato ed eventi</TabsTrigger>
              <TabsTrigger value="edit" className="rounded-none" data-testid="tab-edit">Modifica dati</TabsTrigger>
              <TabsTrigger value="notifications" className="rounded-none" data-testid="tab-notifications">Notifiche</TabsTrigger>
            </TabsList>
            <TabsContent value="events" className="space-y-6 mt-4">
              <EventForm key={s.status + s.updated_at} shipment={s} onUpdated={setS} />
              <div className="bg-white border border-slate-200 p-5 sm:p-6">
                <h3 className="font-display text-lg font-bold uppercase mb-6">Cronologia ({s.events.length})</h3>
                <Timeline events={s.events} admin />
              </div>
            </TabsContent>
            <TabsContent value="edit" className="mt-4">
              <ShipmentForm key={formKey} initial={s} onSubmit={save} submitting={saving} mode="edit" />
            </TabsContent>
            <TabsContent value="notifications" className="mt-4 bg-white border border-slate-200 p-5 sm:p-6">
              <NotifLog code={s.code} />
            </TabsContent>
          </Tabs>
        </div>
        <aside className="xl:col-span-4 order-1 xl:order-2 space-y-6">
          <SharePanel shipment={s} />
          <div className="bg-white border border-slate-200 p-5" data-testid="detail-summary">
            <h3 className="font-display text-lg font-bold uppercase mb-2">Riepilogo</h3>
            <Row label="Mittente" value={s.sender_name} />
            <Row label="Destinatario" value={s.recipient_name} />
            <Row label="Telefono" value={s.recipient_phone} />
            <Row label="Email" value={s.recipient_email} />
            <Row label="Partenza" value={`${s.origin_city}${s.origin_address ? `, ${s.origin_address}` : ""}`} />
            <Row label="Destinazione" value={`${s.destination_city}${s.destination_address ? `, ${s.destination_address}` : ""}`} />
            <Row label="Affidata il" value={fmtDate(s.pickup_date)} />
            <Row label="Consegna prevista" value={fmtDate(s.expected_delivery)} />
            <Row label="Colli / peso" value={`${s.packages} / ${s.weight_kg} kg`} />
            <Row label="Merce" value={s.goods_type} />
            <Row label="Località attuale" value={s.current_location} />
            <Row label="Notifiche consentite" value={s.notify_consent ? "Sì" : "No"} />
            {s.internal_notes && <p className="mt-3 text-sm bg-amber-50 border border-amber-200 p-3 text-amber-900"><strong>Note interne:</strong> {s.internal_notes}</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}
